/**
 * Gestão de Recursos - Edit Page
 *
 * Full edit mode for resource management with 4 sections:
 * 1. Orçamento do Projeto (Budget Form - 9 fields)
 * 2. Plano de Financiamento (Financing Plan Table - 18 sources)
 * 3. Composição de Investimentos (Investment Contracts - dynamic add/delete)
 * 4. Composição de Recursos (Resource Composition - 7 calculated fields)
 *
 * Features:
 * - React Hook Form for state management
 * - Auto-calculations across all sections
 * - Save via API mutation hooks
 * - Cancel button (navigate back to read-only view)
 *
 * Design specs:
 * - Header with Cancel + Save buttons
 * - Same color palette as read-only view
 * - Full form validation before save
 */

import { useState, useEffect } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useLocation } from "wouter";
import { X, Loader2 } from "lucide-react";
import { AppContainer } from "@/components/layout/app-container";
import { Button } from "@/components/ui/button";
import { BudgetFormSection } from "@/components/gestao-recursos/budget-form-section";
import { FinancingPlanTable } from "@/components/gestao-recursos/financing-plan-table";
import { InvestmentContractsSection } from "@/components/gestao-recursos/investment-contracts-section";
import { ResourceCompositionSection } from "@/components/gestao-recursos/resource-composition-section";
import { SaveSuccessModal } from "@/components/gestao-recursos/save-success-modal";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useProjectResources,
  useUpdateResourceComposition,
} from "@/services/queries/useRecursos";
import {
  BudgetSummary,
  FinancingPlanItem,
  InvestmentContractItem,
  ResourceComposition,
} from "@/types/gestao-recursos.interface";
import { useProjetoContext } from "@/context/projeto";

// Form data structure combining all sections
export interface GestaoRecursosEditForm {
  // Section 1: Budget
  budget: BudgetSummary;
  // Section 2: Financing Plan
  financingPlan: FinancingPlanItem[];
  // Section 3: Investment Contracts
  investmentContracts: InvestmentContractItem[];
}

// Initialize 18 financing plan items (one per funding source)
const FUNDING_SOURCES = [
  "Lei nº 8.685/93 - art 1º",
  "Lei nº 8.685/93 - art 1º-A",
  "Lei nº 8.685/93 - art 3º",
  "Lei nº 8.685/93 - art 3º-A",
  "Contribuição de FUNCINES (investimento)",
  "Patrocínio Pessoa Física",
  "Patrocínio Pessoa Jurídica",
  "Público - Federal",
  "Público - Estadual",
  "Público - Municipal",
  "Lei Estadual de Incentivo",
  "Lei Municipal de Incentivo",
  "Patrocínio Privado",
  "Prêmio Internacional",
  "Programa Internacional",
  "Licença de Exibição",
  "Contribuição não financeira",
  "Outros",
];

const initialFinancingPlan: FinancingPlanItem[] = FUNDING_SOURCES.map(
  (fonte) => ({
    fonte,
    valor: 0,
    observacoes: "",
  })
);

export default function GestaoRecursosEditar() {
  const [, setLocation] = useLocation();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Get selected project from context
  const { projetoSelecionado } = useProjetoContext();
  const projectId = projetoSelecionado?.id?.toString() ?? "";

  // Fetch existing project resources
  const { data: resourcesData, isLoading: isLoadingResources } = useProjectResources(projectId);

  // Mutation for saving resource composition
  const updateComposition = useUpdateResourceComposition();

  // Initialize form with default values
  const form = useForm<GestaoRecursosEditForm>({
    defaultValues: {
      budget: {
        desenvolvimento: 0,
        preProducao: 0,
        producao: 0,
        posProducao: 0,
        despesasAdministrativas: 0,
        subtotalFinanciavel: 0,
        taxaGerenciamento: 0,
        taxaAgenciamento: 0,
        totalFinanciaveis: 0,
        orcamentoTotal: 0,
        iniciadoEm: new Date().toISOString().split("T")[0],
        atualizadoEm: new Date().toISOString().split("T")[0],
      },
      financingPlan: initialFinancingPlan,
      investmentContracts: [],
    },
  });

  // Populate form with existing data when API response is available
  useEffect(() => {
    if (resourcesData?.data) {
      const { budgetSummary, financingPlan, investmentsContracted } = resourcesData.data;

      // Merge existing financing plan items with default structure
      const mergedFinancingPlan = initialFinancingPlan.map((defaultItem) => {
        const existingItem = financingPlan?.items?.find(
          (item: FinancingPlanItem) => item.fonte === defaultItem.fonte
        );
        return existingItem || defaultItem;
      });

      form.reset({
        budget: budgetSummary || form.getValues("budget"),
        financingPlan: mergedFinancingPlan,
        investmentContracts: investmentsContracted?.items || [],
      });
    }
  }, [resourcesData, form]);

  // Watch budget totals for ResourceCompositionSection
  const [totalFinanciaveis, orcamentoTotal] = form.watch([
    "budget.totalFinanciaveis",
    "budget.orcamentoTotal",
  ]);

  // Watch investment contracts for total calculation
  const investmentContracts = form.watch("investmentContracts");
  const totalInvestimentos =
    investmentContracts?.reduce((sum, item) => sum + (item?.valor || 0), 0) ||
    0;

  // Handle Cancel - navigate back to read-only view
  const handleCancel = () => {
    setLocation("/financeiro/gestao-recursos");
  };

  // Handle Save - call API mutation
  const handleSave = form.handleSubmit((data) => {
    if (!projectId) return;

    // Transform data to match API format
    const compositionData: ResourceComposition = {
      totalItensFinanciaveis: data.budget.totalFinanciaveis,
      orcamentoTotalProjeto: data.budget.orcamentoTotal,
      totalInvestimentosCaptados: totalInvestimentos,
      valorCaptadoCobertura: data.budget.totalFinanciaveis - totalInvestimentos,
      saldoCaptarTotal: data.budget.orcamentoTotal - totalInvestimentos,
      saldoCaptarFinanciaveis: data.budget.totalFinanciaveis - totalInvestimentos,
      totalFinalProjeto: data.budget.orcamentoTotal - totalInvestimentos,
    };

    updateComposition.mutate(
      { projectId, data: compositionData },
      {
        onSuccess: () => {
          setShowSuccessModal(true);
        },
      }
    );
  });

  // Handle modal continue button - navigate back to read-only view
  const handleContinue = () => {
    setShowSuccessModal(false);
    setLocation("/financeiro/gestao-recursos");
  };

  // Show message if no project is selected
  if (!projectId) {
    return (
      <AppContainer
        title="Editar Recursos do Projeto"
        description="Edite o orçamento, plano de financiamento e composição de investimentos"
      >
        <div className="flex items-center justify-center h-64 text-gray-400">
          Selecione um projeto para editar os recursos
        </div>
      </AppContainer>
    );
  }

  // Show loading skeleton while data is being fetched
  if (isLoadingResources) {
    return (
      <AppContainer
        title="Editar Recursos do Projeto"
        description="Edite o orçamento, plano de financiamento e composição de investimentos"
      >
        <div className="flex flex-col gap-[30px]">
          <Skeleton className="h-[400px] w-full rounded-lg" />
          <Skeleton className="h-[300px] w-full rounded-lg" />
          <Skeleton className="h-[200px] w-full rounded-lg" />
          <Skeleton className="h-[200px] w-full rounded-lg" />
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title="Editar Recursos do Projeto"
      description="Edite o orçamento, plano de financiamento e composição de investimentos"
      button={
        <>
          <Button
            type="button"
            onClick={handleCancel}
            variant="outline"
            className="border-[#DC4426] text-[#DC4426] hover:bg-[#DC4426] hover:text-white h-[50px] w-[173px] text-[18px] font-bold"
          >
            <X size={24} className="mr-2" />
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateComposition.isPending}
            className="bg-[#7846FF] hover:bg-[#9b6fff] text-[#F5F2FF] h-[50px] w-[173px] text-[18px] font-bold disabled:opacity-50"
          >
            {updateComposition.isPending ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Salvando...
              </>
            ) : (
              "Salvar"
            )}
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-[30px]">
        {/* Section 1: Orçamento do Projeto */}
        <BudgetFormSection form={form} />

        {/* Section 2: Plano de Financiamento */}
        <FinancingPlanTable form={form} />

        {/* Section 3: Composição de Investimentos e Patrocínios Contratados */}
        <InvestmentContractsSection form={form} />

        {/* Section 4: Composição de Recursos (Calculated Fields) */}
        <ResourceCompositionSection
          totalFinanciaveis={totalFinanciaveis || 0}
          orcamentoTotal={orcamentoTotal || 0}
          totalInvestimentos={totalInvestimentos}
        />
      </form>

      {/* Success Modal */}
      <SaveSuccessModal open={showSuccessModal} onContinue={handleContinue} />
    </AppContainer>
  );
}
